import PropTypes from "prop-types";
import {useT} from "../i18n/i18n";
import {Link} from "react-router-dom";

export default function TermsOfService({language}) {
    const {t} = useT(language);

    return (
        <section className="mx-auto max-w-4xl py-12 sm:py-16">
            <article className="prose prose-invert max-w-none">
                <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl mb-2 text-[rgb(var(--app-fg))]">
                    {t("terms.title")}
                </h1>
                <p className="text-sm text-[rgb(var(--app-muted))] mb-8">
                    {t("terms.lastUpdated")}: June 27, 2026
                </p>

                <div className="space-y-8 text-[rgb(var(--app-muted))]">
                    {/* Introduction */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.intro")}
                        </h2>
                        <p className="leading-relaxed">{t("terms.introText")}</p>
                    </section>

                    {/* Service Description */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.serviceDescription")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("terms.serviceDescriptionText")}</p>
                        <p className="leading-relaxed">{t("terms.emailText")}</p>
                    </section>

                    {/* Use Restrictions */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.useRestrictions")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("terms.acceptableUseText")}</p>
                        <div className="bg-white/10 dark:bg-white/10 p-5 rounded-lg border border-white/20 backdrop-blur-sm text-sm">
                            <p className="leading-relaxed mb-4 text-white">
                                <strong className="text-white block mb-2">
                                    {language === "en" ? "Prohibited Activities:" : "Attività Proibite:"}
                                </strong>
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-white/90">
                                <li>{language === "en" ? "Unlawful or fraudulent activities" : "Attività illegittime o fraudolente"}</li>
                                <li>{language === "en" ? "Transmitting malware or harmful content" : "Trasmissione di malware o contenuti dannosi"}</li>
                                <li>{language === "en" ? "Unauthorized system access attempts" : "Tentativi di accesso non autorizzati"}</li>
                                <li>{language === "en" ? "Spam or harassment" : "Spam o molestie"}</li>
                                <li>{language === "en" ? "Circumventing security measures" : "Elusione delle misure di sicurezza"}</li>
                            </ul>
                        </div>
                    </section>

                    {/* Intellectual Property */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.intellectualProperty")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("terms.intellectualPropertyText")}</p>
                        <p className="leading-relaxed text-sm bg-white/10 dark:bg-white/10 p-4 rounded-lg border border-white/20 backdrop-blur-sm text-white">
                            <strong className="text-white block mb-2">
                                {language === "en" ? "Licensed Use:" : "Uso Concesso in Licenza:"}
                            </strong>
                            <span className="text-white/90">
                                {language === "en"
                                    ? "You may view and download content for personal, non-commercial use only."
                                    : "Puoi visualizzare e scaricare contenuti solo per uso personale e non commerciale."}
                            </span>
                        </p>
                    </section>

                    {/* Disclaimers */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.disclaimers")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("terms.disclaimersText")}</p>
                        <div className="bg-white/10 dark:bg-white/10 p-5 rounded-lg border border-white/20 backdrop-blur-sm text-sm">
                            <p className="leading-relaxed mb-3 text-white">
                                <strong className="text-white block mb-2">{language === "en" ? "AS IS Disclaimer:" : "Esclusione di Garanzie:"}</strong>
                            </p>
                            <p className="text-white/90 leading-relaxed">
                                {language === "en"
                                    ? "This website and all services are provided on an \"as is\" and \"as available\" basis. To the fullest extent permitted by law, the operator disclaims all warranties, whether express or implied."
                                    : "Questo sito e tutti i servizi sono forniti \"così come sono\" e \"come disponibili\". Nella massima misura consentita dalla legge, l'operatore declina ogni garanzia, espressa o implicita."}
                            </p>
                        </div>
                    </section>

                    {/* Limitation of Liability */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.limitation")}
                        </h2>
                        <p className="leading-relaxed">{t("terms.liabilityText")}</p>
                    </section>

                    {/* Governing Law */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.governing")}
                        </h2>
                        <p className="leading-relaxed">{t("terms.governingText")}</p>
                    </section>

                    {/* Contact Us */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("terms.sections.contact")}
                        </h2>
                        <p className="leading-relaxed mb-3">{t("terms.contactUsText")}</p>
                        <div className="bg-white/10 dark:bg-white/10 p-5 rounded-lg border border-white/20 backdrop-blur-sm">
                            <p className="font-semibold text-white mb-3">Yurii Suprun</p>
                            <p className="text-white/90 flex items-center gap-2 mb-2">
                                <svg className="w-5 h-5 text-white/70 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                </svg>
                                <a href="mailto:iursuprun@gmail.com" className="hover:text-white transition-colors underline decoration-white/40 hover:decoration-white">
                                    iursuprun@gmail.com
                                </a>
                            </p>
                            <p className="text-sm text-white/80">
                                P.IVA (Italian Tax ID): 13728820963
                            </p>
                        </div>
                    </section>

                    {/* Links to Legal Documents */}
                    <section className="pt-6 border-t border-black/10 dark:border-white/15">
                        <p className="text-center text-sm">
                            <Link
                                to="/privacy-policy"
                                className="text-[rgb(var(--app-accent))] hover:underline"
                            >
                                {language === "en" ? "View Privacy Policy" : "Visualizza la Privacy Policy"}
                            </Link>
                        </p>
                    </section>
                </div>
            </article>
        </section>
    );
}

TermsOfService.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};
